import React from 'react';
import { chartColors } from '@fabric-ui/theme';
import {
  ChartAccessors,
  ChartDimensions,
  ChartRootProps,
  ChartSeries,
  WithColorAccessor,
  WithDataLabels,
  WithLegend,
  WithPieVariant,
} from '../../core/Chart.types';
import { ChartContainer, useChartContainerContext } from '../../core/ChartContainer';
import { ChartRoot, useChartAccessorsContext } from '../../core/ChartRoot';
import { ChartSurface } from '../../core/ChartSurface';
import { ChartWrapper } from '../../core/ChartWrapper';
import { Legend, LegendSlotProps } from '../../core/legend/Legend';
import { RenderTooltip } from '../../core/RenderTooltip';
import { Tooltip } from '../../core/tooltip/Tooltip';
import { usePinnedDismissal } from '../../core/tooltip/useChartTooltip';
import { defaultIdAccessor } from '../../utils/defaultGetIdentifier';
import { useEnsureDatumIdentifier } from '../../utils/useEnsureDatumIdentifier';
import { useGetColorAccessor } from '../../utils/useGetColorAccessor';
import { DonutCenterContentProps, Pie } from './PieVisx';

type PieSeriesProps<Datum, TooltipProps = {}> = Omit<ChartRootProps<NoInfer<Datum>>, 'colorAccessor' | 'idAccessor'> &
  ChartSeries<Datum> &
  Partial<WithColorAccessor<NoInfer<Datum>>> &
  Partial<Pick<ChartAccessors<NoInfer<Datum>>, 'idAccessor'>> &
  Partial<ChartDimensions> &
  WithDataLabels &
  WithLegend &
  WithPieVariant &
  DonutCenterContentProps<NoInfer<Datum>> &
  Pick<React.ComponentProps<typeof Tooltip<NoInfer<Datum>, TooltipProps>>, 'TooltipComponent' | 'tooltipComponentProps'> & {
    total?: number;
    slotProps?: LegendSlotProps<NoInfer<Datum>>;
  };

export const PieSeries = <Datum, TooltipProps = {}>({
  data: _data,
  idAccessor = defaultIdAccessor,
  colorAccessor: _colorAccessor,
  width,
  height,
  palette = chartColors,
  dataLabelMode,
  legendType = 'linear-top',
  onTooltipClose,
  onItemClick,
  highlightedItem,
  onHighlightChange,
  pinOnClick,
  variant,
  centerValue,
  centerLabel,
  renderCenter,
  total,
  slotProps,
  TooltipComponent = RenderTooltip,
  tooltipComponentProps,
  ...props
}: PieSeriesProps<Datum, TooltipProps>) => {
  const data = useEnsureDatumIdentifier(_data, idAccessor);
  const getColor = useGetColorAccessor(data, idAccessor, _colorAccessor, palette);

  return (
    <ChartRoot
      idAccessor={idAccessor}
      colorAccessor={getColor}
      onTooltipClose={onTooltipClose}
      onItemClick={onItemClick}
      highlightedItem={highlightedItem}
      onHighlightChange={onHighlightChange}
      pinOnClick={pinOnClick}
      {...props}>
      <ChartWrapper legendType={legendType}>
        <ChartContainer width={width} height={height}>
          <ChartSurface>
            <PieChart
              data={data}
              dataLabelMode={dataLabelMode}
              variant={variant}
              centerValue={centerValue}
              centerLabel={centerLabel}
              renderCenter={renderCenter}
              total={total}
            />
            <Tooltip
              TooltipComponent={TooltipComponent}
              tooltipComponentProps={tooltipComponentProps as React.ComponentProps<typeof TooltipComponent>}
            />
          </ChartSurface>
        </ChartContainer>
        {legendType !== 'none' && <Legend data={data} legendType={legendType} total={total} slotProps={slotProps} />}
      </ChartWrapper>
    </ChartRoot>
  );
};

type PieChartInnerProps<Datum> = {
  data: Datum[];
  dataLabelMode?: PieSeriesProps<Datum>['dataLabelMode'];
  total?: number;
} & WithPieVariant &
  DonutCenterContentProps<Datum>;

const PieChart = <Datum,>({ data, dataLabelMode, variant, centerValue, centerLabel, renderCenter, total }: PieChartInnerProps<Datum>) => {
  const accessors = useChartAccessorsContext<Datum>();
  const dimensions = useChartContainerContext();
  usePinnedDismissal();

  return (
    <Pie
      data={data}
      dataLabelMode={dataLabelMode}
      variant={variant}
      centerValue={centerValue}
      centerLabel={centerLabel}
      renderCenter={renderCenter}
      total={total}
      {...accessors}
      {...dimensions}
    />
  );
};
